import { TradeMethodType } from "../../trade/method";
import { AirdropFarmingBotType } from "../airdrop-farming";

/**
 * Payload for TRADE_EXECUTED event
 *
 * This is the unified structure used across all trade types (CEX, Hyperliquid, Onchain).
 * The `chain` field distinguishes between trade sources.
 *
 */
export interface TradeExecutedPayload {
  // === Core identifiers (required) ===
  id: string;
  /**
   * Exchange ID of the trade.
   */
  exchangeId: string;
  /**
   * Pair symbol of the trade.
   */
  pairSymbol: string;

  // === Trade details ===
  /**
   * Expected price of the trade. This is what
   * the user sees in the trade page.
   */
  expectedPrice: string | number;
  amount: string | number;
  /**
   * Type of the trade. "limit" or "market".
   */
  type: string;
  /**
   * Side of the trade. "buy" or "sell".
   */
  side: string;
  status?: string;
  /**
   * Price of the trade. This is the price at
   * which the trade was executed.
   */
  price: string | number;
  /**
   * Only for Perps markets.
   * The base amount without leverage.
   * For perps: initialBaseAmount = baseAmount / leverage
   * For spot: initialBaseAmount = baseAmount
   */
  initialBaseAmount?: number;
  /**
   * Base amount of the trade. This is the amount
   * of the base currency traded.
   */
  baseAmount: string | number;
  /**
   * Only for Futures markets.
   * The USD equivalent without leverage (margin/collateral amount).
   * For perps: baseFiatAmount = initialBaseFiatAmount × leverage
   * For spot: baseFiatAmount = notionalVolume
   */
  initialBaseFiatAmount?: number;
  /**
   * The USD equivalent of the base amount.
   */
  baseFiatAmount: number | "unknown";
  /**
   * Type of the market. "perps" or "spot".
   */
  marketType: "perps" | "spot";

  // === User context ===
  walletAddress?: string;
  exchangeUserId?: string;

  // === Tracking metadata (required) ===
  tradingMethod: TradeMethodType;
  timestamp: string | number;

  /**
   * Chain identifier for trade events
   * - "offchain": CEX trades (Binance, Kucoin, etc.)
   * - "hyperliquid": Hyperliquid DEX
   * - string (chainId): Onchain trades (e.g., "8453" for Base)
   */
  chain: string;

  // === Campaign context (optional) ===
  /**
   * Whether the trade was initiated from the standalone trade page (not from a campaign).
   */
  fromTradePage: boolean;
  campaignName?: string;
  campaignType?: string;
  campaignCategory?: "trade-to-earn" | "hold-to-earn";
  epochNumber?: number | null;

  // === Referral context (optional) ===
  referrerId?: string | number;
  referralStatus?: string;

  // === CEX-specific (optional) ===

  // === Autopilot context (optional) ===
  /**
   * Whether the trade was executed by the automatic streak (autopilot) feature.
   */
  autopilot?: boolean;
  /**
   * The automatic streak ID, if this trade was executed by autopilot.
   */
  streakId?: string;

  // === Hyperliquid-specific (optional) ===
  accountId?: number;
  leverage?: number;
  marginMode?: "cross" | "isolated";
  reduceOnly?: boolean;
  isClosingPosition?: boolean;
  isOpeningPosition?: boolean;

  // === Onchain-specific (optional) ===
  tokenInAddress?: string;
  tokenOutAddress?: string;
  amountOut?: string;

  // === Allow additional properties from orderBookComputedValues spread ===
  [key: string]: unknown;
}

/**
 * Payload for TRADE_FAILED event
 *
 * Same unified structure as TRADE_EXECUTED but for failed trades.
 */
export interface TradeFailedPayload {
  // === Core identifiers ===
  exchangeId: string;
  pairSymbol?: string;

  // === Trade details ===
  price?: string | number;
  amount?: string | number;
  type?: string;
  side?: string;
  marketType?: "perps" | "spot";

  // === User context ===
  walletAddress?: string;

  // === Tracking metadata (required) ===
  tradingMethod: TradeMethodType;
  errorMessage: string;
  /**
   * Chain identifier for trade events
   * - "offchain": CEX trades (Binance, Kucoin, etc.)
   * - "hyperliquid": Hyperliquid DEX
   * - string (chainId): Onchain trades (e.g., "8453" for Base)
   */
  chain: string;

  // === Campaign context (optional) ===
  campaignName?: string;
  campaignType?: string;
  campaignCategory?: "trade-to-earn" | "hold-to-earn";
  epochNumber?: number | null;

  // === Referral context (optional) ===
  referrerId?: string | number;
  referralStatus?: string;

  // === CEX-specific (optional) ===
  fromTradePage?: boolean;

  // === Autopilot context (optional) ===
  /**
   * Whether the trade was executed by the automatic streak (autopilot) feature.
   */
  autopilot?: boolean;
  /**
   * The automatic streak ID, if this trade was executed by autopilot.
   */
  streakId?: string;

  // === Hyperliquid-specific (optional) ===
  accountId?: number;
  leverage?: number;
  marginMode?: "cross" | "isolated";
  reduceOnly?: boolean;
  isClosingPosition?: boolean;
  isOpeningPosition?: boolean;

  // === Onchain-specific (optional) ===
  tokenInAddress?: string;
  tokenOutAddress?: string;

  // === Allow additional properties ===
  [key: string]: unknown;
}

/**
 * Payload for AIRDROP_FARMING_BOT_CREATED event.
 * Fired when a new airdrop farming bot is successfully created.
 */
export interface AirdropFarmingBotCreatedPayload {
  distinct_id: string;
  bot_id: number;
  hub_user_id: number;
  exchange_id: string;
  bot_type: AirdropFarmingBotType;
  platform: "cex" | "onchain";
  strategy_type: string;
  pair: string;
  margin_mode: "cross" | "isolated" | null;
  capital_usdc: string;
  leverage: number;
  target_volume_usdc: string;
  max_loss_usdc: string;
  risk_profile: string;
  grid_spread_bps: string;
  num_levels: number;
  stop_loss_distance_bps: number;
  regrid_drift_bps: string;
  max_exposure_pct: number;
  unwind_chunks: number;
  created_at: string;
}

/**
 * Payload for AIRDROP_FARMING_BOT_PAUSED event.
 * Fired when the bot is paused due to high exposure.
 */
export interface AirdropFarmingBotPausedPayload {
  distinct_id: string;
  bot_id: number;
  hub_user_id: number;
  exchange_id: string;
  bot_type: AirdropFarmingBotType;
  platform: "cex" | "onchain";
  strategy_type: string;
  pair: string;
  exposure_pct: number;
  max_exposure_pct: number;
  capital_usdc: string;
  leverage: number;
  risk_profile: string;
  total_volume_usdc: string;
  realized_pnl_usdc: string;
  unrealized_pnl_usdc: string;
}

/**
 * Payload for AIRDROP_FARMING_BOT_FINALIZED event.
 * Fired when the bot reaches a terminal state.
 */
export interface AirdropFarmingBotFinalizedPayload {
  distinct_id: string;
  bot_id: number;
  hub_user_id: number;
  exchange_id: string;
  bot_type: AirdropFarmingBotType;
  platform: "cex" | "onchain";
  strategy_type: string;
  pair: string;
  final_status: "STOPPED" | "COMPLETED" | "STOPPED_MAX_LOSS" | "ERROR";
  stop_reason: "user" | "max_loss" | "volume_target" | "error" | "admin" | "exchange_maintenance" | null;
  error_reason?: string | null;
  error_message?: string | null;
  capital_usdc: string;
  leverage: number;
  target_volume_usdc: string;
  total_volume_usdc: string;
  fill_rate_pct: number;
  max_loss_usdc: string;
  realized_pnl_usdc: string;
  unrealized_pnl_usdc: string;
  total_fees_usdc: string;
  risk_profile: string;
  margin_mode: "cross" | "isolated" | null;
  created_at: string;
  completed_at: string | null;
  running_duration_ms: number | null;
}

/**
 * Payload for AUTOMATIC_STREAK_ENDED event.
 * Unified event for streak completion/stop/failure.
 */
export interface AutomaticStreakEndedPayload {
  // === Core identifiers (required) ===
  distinct_id: string;
  streak_id: string;
  campaign_name: string;
  pair_symbol: string;

  // === Streak configuration ===
  total_trades: number;
  interval_seconds: number;
  volume_min: string;
  volume_max: string;

  // === Streak results ===
  completed_trades: number;
  skipped_cycles: number; //Cycles skipped due to spread threshold
  total_volume_traded: string;

  // === Outcome properties ===
  /**
   * Final status of the streak.
   * - "COMPLETED": All trades executed successfully
   * - "STOPPED": Streak was stopped before completion
   */
  status: "COMPLETED" | "STOPPED";

  /**
   * Human-friendly outcome for analytics.
   * - "success": Streak completed all trades successfully
   * - "stopped": Streak was stopped due to external conditions (epoch ended, user cancelled, etc.)
   * - "failed": Streak stopped due to trade execution failure
   */
  outcome: "success" | "stopped" | "failed";

  /**
   * Reason for stopping the streak.
   * Present for stopped/failed outcomes.
   */
  stop_reason?: string;

  /**
   * Error message from trade execution.
   * Present for failed outcomes.
   */
  error_message?: string;
}

export const METRICS_EVENTS = {
  // Trading Events
  TRADE_EXECUTED: "Trade Executed",
  TRADE_FAILED: "Trade Failed",
  REFRESH_LIMIT_ORDER: "Refresh Limit Order",
  TRADE_WARNING: "Trade Warning",
  DEPOSIT_BUTTON_CLICKED: "Trade Widget: Deposit Button Clicked",

  // Predictions Events
  VIEW_PREDICTIONS_PAGE: "Predictions - View Page",
  PREDICTIONS_CATEGORY_SELECTED: "Predictions - Category Selected",
  PREDICTIONS_CONNECT_CLICKED: "Predictions - Connect Clicked",
  PREDICTIONS_DEPOSIT_CLICKED: "Predictions - Deposit Clicked",
  PREDICTIONS_WITHDRAW_CLICKED: "Predictions - Withdraw Clicked",
  PREDICTIONS_POSITIONS_CLICKED: "Predictions - Positions Clicked",
  PREDICTIONS_DISCONNECT_CLICKED: "Predictions - Disconnect Clicked",
  PREDICTIONS_TRADE_MODAL_OPENED: "Predictions - Trade Modal Opened",
  PREDICTIONS_TRADE_SUBMIT_CLICKED: "Predictions - Trade Submit Clicked",
  PREDICTIONS_DEPOSIT_TO_TRADE_CLICKED: "Predictions - Deposit To Trade Clicked",
  PREDICTIONS_DEPOSIT_SUBMIT_CLICKED: "Predictions - Deposit Submit Clicked",
  PREDICTIONS_WITHDRAW_SUBMIT_CLICKED: "Predictions - Withdraw Submit Clicked",
  PREDICTIONS_ORDER_EXECUTED: "Predictions - Order Executed",
  PREDICTIONS_ORDER_EXECUTED_SERVER_SIDE: "Predictions - Order Executed - Server Side",
  PREDICTIONS_ORDER_FAILED: "Predictions - Order Failed",

  // Market Selector Events
  MARKET_SELECTOR_OPENED: "Select Market - Opened",
  MARKET_SELECTOR_TAB_CHANGED: "Select Market - Tab Selected",
  MARKET_SELECTOR_EXCHANGE_CHANGED: "Select Market - Exchange Selected",
  MARKET_SELECTOR_MARKET_SELECTED: "Select Market - Market Selected",
  MARKET_SELECTOR_SEARCH: "Select Market - Search",
  MARKET_SELECTOR_FAVORITES_TAB_CLICKED: "Select Market - Favorites Tab Clicked",
  MARKET_SELECTOR_FAVORITE_ADDED: "Select Market - Favorite Added",
  MARKET_SELECTOR_FAVORITE_REMOVED: "Select Market - Favorite Removed",

  // Hyperliquid Trading Events
  HYPERLIQUID_POSITION_CLOSED: "Hyperliquid - Position Closed",
  HYPERLIQUID_ORDER_CANCELED: "Hyperliquid - Order Canceled",
  HYPERLIQUID_TAB_VIEWED: "Hyperliquid - Tab Viewed",
  HYPERLIQUID_ACTIVATE_ACCOUNT_MODAL_OPENED: "Hyperliquid - Activate Account Modal Opened",
  HYPERLIQUID_DEPOSIT_SUBMITTED: "Hyperliquid - Deposit Submitted",
  HYPERLIQUID_MARGIN_MODE_CHANGED: "Hyperliquid - Margin Mode Changed",

  // CEX Perps Trading Events
  CEX_PERPS_POSITION_CLOSED: "CEX Perps - Position Closed",
  CEX_PERPS_TAB_VIEWED: "CEX Perps - Tab Viewed",

  // Spot Trading Events
  SPOT_TRADING_TAB_VIEWED: "Spot Trading - Tab Viewed",
  SPOT_TRADING_PAGINATION_CLICKED: "Spot Trading - Pagination Clicked",
  // Perps Account Overview Events
  PERPS_ACCOUNT_OVERVIEW_DEPOSIT_CLICKED: "Perps Account Overview - Deposit Clicked",
  PERPS_ACCOUNT_OVERVIEW_TRANSFER_CLICKED: "Perps Account Overview - Transfer Clicked",
  PERPS_ACCOUNT_OVERVIEW_WITHDRAW_CLICKED: "Perps Account Overview - Withdraw Clicked",

  // Onchain Swap Events
  ONCHAIN_SWAP_TOKEN_APPROVED: "Onchain Swap - Token Approved",
  ONCHAIN_SWAP_SUBMIT_FAILED: "Onchain Swap - Submit Failed",
  ONCHAIN_SWAP_HIGH_PRICE_IMPACT_WARNING: "Onchain Swap - High Price Impact Warning",

  // Bridge Events
  BRIDGE_MODAL_OPENED: "Bridge Modal - Opened",
  BRIDGE_SERVICE_SELECTED: "Bridge Service - Selected",
  BRIDGE_ROUTE_EXECUTION_STARTED: "Bridge Route - Execution Started",
  BRIDGE_ROUTE_EXECUTION_UPDATED: "Bridge Route - Execution Updated",
  BRIDGE_ROUTE_EXECUTION_COMPLETED: "Bridge Route - Execution Completed",
  BRIDGE_ROUTE_EXECUTION_FAILED: "Bridge Route - Execution Failed",
  BRIDGE_ROUTE_HIGHLIGHTED: "Bridge Route - Highlighted",
  BRIDGE_ROUTE_SELECTED: "Bridge Route - Selected",
  BRIDGE_SEND_TO_WALLET_TOGGLED: "Bridge - Send to Wallet Toggled",
  BRIDGE_DESTINATION_WALLET_CHANGED: "Bridge - Destination Wallet Changed",
  BRIDGE_CONTACT_SUPPORT_CLICKED: "Bridge - Contact Support Clicked",
  BRIDGE_SOURCE_TOKEN_SELECTED: "Bridge - Source Token Selected",
  BRIDGE_DESTINATION_TOKEN_SELECTED: "Bridge - Destination Token Selected",

  // Page View Events
  VIEW_MAIN_PAGE: "View Main page",
  VIEW_CAMPAIGNS_PAGE: "View Campaigns page",
  VIEW_CAMPAIGN: "View Campaign",
  VIEW_HOLD_CAMPAIGNS_PAGE: "View Hold Campaigns page",
  VIEW_HOLD_CAMPAIGN: "View Hold Campaign",
  VIEW_LOTTERY_PAGE: "View Lottery page",
  VIEW_PROFILE_PAGE: "View Profile page",
  VIEW_PORTFOLIO_PAGE: "View Portfolio page",
  VIEW_SELF_HOST_CAMPAIGN_PAGE: "View Self Host Campaign Page",

  // Self-hosted Campaign Events
  SELF_HOST_CTA_CLICKED: "Self Host Campaign - CTA Clicked",
  SELF_HOST_LAUNCH_CAMPAIGN_CLICKED: "Self Host Campaign - Launch Clicked",
  SELF_HOST_BACK_TO_CAMPAIGNS_CLICKED: "Self Host Campaign - Back to Campaigns Clicked",
  SELF_HOST_FORM_STEP_VIEWED: "Self Host Campaign - Form Step Viewed",
  SELF_HOST_FORM_STEP_CONTINUED: "Self Host Campaign - Form Step Continued",
  SELF_HOST_FORM_STEP_BACK: "Self Host Campaign - Form Step Back",
  SELF_HOST_FORM_RESET: "Self Host Campaign - Form Reset",
  SELF_HOST_FORM_EXIT: "Self Host Campaign - Form Exit",
  SELF_HOST_REVIEW_EDIT_CLICKED: "Self Host Campaign - Review Edit Clicked",
  SELF_HOST_REVIEW_BACK_CLICKED: "Self Host Campaign - Review Back Clicked",
  SUBMIT_SELF_HOST_CAMPAIGN_REQUEST: "Self Host Campaign - Submit Request",
  SELF_HOST_CAMPAIGN_CONFIRMED: "Self Host Campaign - Confirmed",
  SELF_HOST_COPY_REQUEST_NUMBER: "Self Host Campaign - Copy Request Number",
  CONTACT_BD_TELEGRAM_CLICKED: "Self Host Campaign - Contact BD Telegram Clicked",
  SELF_HOST_EMAIL_BD_CLICKED: "Self Host Campaign - Email BD Clicked",
  SELF_HOST_SUCCESS_GO_TO_CAMPAIGNS: "Self Host Campaign - Success Go to Campaigns",

  // Crypto.com Referee Flow Events
  CLICK_CRYPTO_COM_CTA: "Crypto.com Referee - CTA Clicked",
  CRYPTO_COM_REFEREE_PAGE_VIEWED: "Crypto.com Referee - Page Viewed",
  CRYPTO_COM_REFEREE_SIGNUP_CLICKED: "Crypto.com Referee - Signup Link Clicked",
  CRYPTO_COM_OAUTH_CONNECTED: "Crypto.com Referee - OAuth Connected",
  CRYPTO_COM_REVIEW_SUBMITTED: "Crypto.com Referee - Review Submitted",
  CRYPTO_COM_REFEREE_SUPPORT_CLICKED: "Crypto.com Referee - Support Link Clicked",

  VIEW_QUESTS_PAGE: "View Quests page",
  VIEW_REFERRALS_PAGE: "View Referrals page",
  VIEW_HALL_OF_FAME_PAGE: "View Hall of Fame page",
  VIEW_TERMS_AND_CONDITIONS_PAGE: "View Terms and Conditions page",
  VIEW_TRADE_PAGE: "View Trade page",
  VIEW_HYPERLIQUID_PAGE: "View Hyperliquid page",
  VIEW_ONCHAIN_SWAP_PAGE: "View Onchain Swap page",
  VIEW_PRIVACY_POLICY_PAGE: "View Privacy Policy page",
  VIEW_CLICK_AND_EARN_PAGE: "View Click and Earn",

  // Currency Events
  CURRENCY_SELECTED: "Currency - Selected",

  // Social Account Events
  SOCIAL_ACCOUNT_TOO_YOUNG: "Social Account Too Young",
  SOCIAL_ACCOUNT_UNLINKED: "Social Account Unlinked",
  SOCIAL_ACCOUNT_CONNECTED: "Social account linked",
  FLOW_DISCORD_CONNECTED: "Flow - Discord Connected",
  FLOW_DISCORD_DISCONNECTED: "Flow - Discord Disconnected",
  FLOW_X_CONNECTED: "Flow - X Connected",
  FLOW_X_DISCONNECTED: "Flow - X Disconnected",
  FLOW_TELEGRAM_CONNECTED: "Flow - Telegram Connected",
  FLOW_TELEGRAM_DISCONNECTED: "Flow - Telegram Disconnected",
  FLOW_VIEW_PORTFOLIO_PAGE: "Flow - View Portfolio Page",

  // Voucher Events
  VOUCHER_REDEEM_SUCCESS: "Voucher Redeem - Success",
  VOUCHER_REDEEM_FAILED: "Voucher Redeem - Failed",

  // Onboarding Events
  ONBOARDING_STARTED: "Onboarding - Started",
  ONBOARDING_CLOSED: "Onboarding - Closed",
  ONBOARDING_COMPLETED: "Onboarding - Completed",
  ONBOARDING_SKIP_CLICKED: "Onboarding - Skip Clicked",
  ONBOARDING_CONTINUE_CLICKED: "Onboarding - Continue Clicked",
  ONBOARDING_BACK_CLICKED: "Onboarding - Back Clicked",
  ONBOARDING_CEX_SELECTED: "Onboarding - CEX Selected",
  ONBOARDING_CEX_STEP_VIEWED: "Onboarding - CEX Step Viewed",
  ONBOARDING_SOCIAL_STEP_VIEWED: "Onboarding - Social Step Viewed",
  ONBOARDING_SOCIAL_PLATFORM_SELECTED: "Onboarding - Social Platform Selected",
  ONBOARDING_WALLET_CONNECT_CLICKED: "Onboarding - Wallet Connect Clicked",
  ONBOARDING_WALLET_DISCONNECTED: "Onboarding - Wallet Disconnected",
  ONBOARDING_REFERRAL_CODE_SAVED: "Onboarding - Referral Code Saved",
  ONBOARDING_REFERRAL_TOGGLE_OPENED: "Onboarding - Referral Toggle Opened",

  // Wallet Connection Events
  CONNECT_WALLET_OPEN: "Connect Wallet - Open",
  CONNECT_ACCOUNT_OPEN: "Connect Account - Open",
  CONNECT_ACCOUNT_CLOSE: "Connect Account - Close",
  CONNECT_ACCOUNT_NEXT: "Connect Account - Next",
  CONNECT_ACCOUNT_BACK: "Connect Account - Back",
  PREDICTIONS_CONNECTION_STARTED: "Predictions - Connection Started",
  PREDICTIONS_NETWORK_SWITCHED: "Predictions - Network Switched",
  PREDICTIONS_CONNECTION_SUCCESS: "Predictions - Connection Success",
  PREDICTIONS_CONNECTION_FAILED: "Predictions - Connection Failed",
  PREDICTIONS_ONBOARDING_DEPLOY_CLICKED: "Predictions - Onboarding Deploy Clicked",
  PREDICTIONS_ONBOARDING_APPROVE_CLICKED: "Predictions - Onboarding Approve Clicked",
  PREDICTIONS_ONBOARDING_CREDENTIALS_CLICKED: "Predictions - Onboarding Credentials Clicked",
  PREDICTIONS_ONBOARDING_FINISH_CLICKED: "Predictions - Onboarding Finish Clicked",
  PREDICTIONS_ONBOARDING_ERROR_DISMISSED: "Predictions - Onboarding Error Dismissed",

  // Account Events
  ACCOUNT_ADDED: "Account Added",
  CONNECT_SOCIAL_IN_CAMPAIGN_STEPPER_MODAL_LINK: "Connect Social in campaign stepper modal - Link",
  PRIVY_LOGIN_ERROR: "Privy Login - Error",

  // Campaign Events
  CAMPAIGNS_MENU_EXCHANGE_SELECTED: "CampaignsMenu:Exchange:Selected",
  CAMPAIGNS_MENU_STATUS_SELECTED: "CampaignsMenu:Status:Selected",
  CAMPAIGNS_MENU_PARTICIPATION_FILTER_SELECTED: "CampaignsMenu:ParticipationFilter:Selected",
  CAMPAIGNS_MENU_PARTICIPATION_FILTER_CLEARED: "CampaignsMenu:ParticipationFilter:Cleared",
  CAMPAIGNS_MENU_RULE_SELECTED: "CampaignsMenu:Rule:Selected",
  CAMPAIGNS_MENU_SORTING_SELECTED: "CampaignsMenu:Sorting:Selected",
  CAMPAIGNS_TABLE_NEXT_PAGE: "CampaignsTable:NextPage",
  CAMPAIGNS_TABLE_PREVIOUS_PAGE: "CampaignsTable:PreviousPage",
  CAMPAIGNS_TABLE_ROWS_PER_PAGE_CHANGE: "CampaignsTable:RowsPerPageChange",
  CAMPAIGNS_SEARCH: "Campaigns:Search",
  CAMPAIGNS_SEARCH_CLEARED: "Campaigns:SearchCleared",
  CAMPAIGN_WHITELIST_BANNER_CLICKED: "Campaign Whitelist Banner - Clicked",
  CAMPAIGN_CARD_DETAILS_CLICK: "Campaign:CardDetailsClick",

  // Hold Campaign Events
  HOLD_CAMPAIGNS_TABLE_NEXT_PAGE: "HoldCampaignsTable:NextPage",
  HOLD_CAMPAIGNS_TABLE_PREVIOUS_PAGE: "HoldCampaignsTable:PreviousPage",
  HOLD_CAMPAIGNS_TABLE_ROWS_PER_PAGE_CHANGE: "HoldCampaignsTable:RowsPerPageChange",
  JOIN_HOLD_CAMPAIGN_SUCCESS: "Join Hold Campaign - Success",
  JOIN_HOLD_CAMPAIGN_FAILED: "Join Hold Campaign - Failed",

  // Campaign View Toggle Events
  TRADE_TRADING_VIEW_TOGGLE: "Trade:TradingViewToggle",
  TRADE_ORDER_BOOK_TOGGLE: "Trade:OrderBookToggle",
  TRADE_ORDER_BOOK_DETAILS_TOGGLE: "Trade:OrderBookDetailsToggle",

  // Campaign Partner Events
  PARTNER_MODAL_OPEN: "Partner Modal - Open",
  PARTNER_MODAL_CLOSE_OUTSIDE_MODAL: "Partner Modal - Close outside modal",
  PARTNER_MODAL_CLOSE_ON_CONTINUE: "Partner Modal - Close on Continue",
  PROJECT_EXTERNAL_LINK_CLICKED: "Project External Link Clicked",

  // CEX Banner Events
  CEX_TOKEN_INFO_BANNER_DISMISSED: "CEX Token Info Banner - Dismissed",
  CEX_TOKEN_INFO_BANNER_BUY_BUTTON_CLICKED: "CEX Token Info Banner - Buy Button Clicked",
  CEX_FEE_REDUCTION_BANNER_DISMISSED: "CEX Fee Reduction Banner - Dismissed",
  CEX_FEE_REDUCTION_BANNER_LEARN_MORE_CLICKED: "CEX Fee Reduction Banner - Learn More Clicked",

  // CEX Token Info Modal Events
  CEX_TOKEN_INFO_MODAL_BUY_TOKEN_CLICKED: "CEX Token Info Modal - Buy Token Clicked",
  CEX_TOKEN_INFO_MODAL_LEARN_MORE_CLICKED: "CEX Token Info Modal - Learn More Clicked",

  // Trade Details Events
  TRADE_DETAILS_BUY_NATIVE_TOKEN_CLICKED: "Trade Details - Buy Native Token Clicked",

  // Referral Events
  REFERRAL_CODE_SUBMITTED: "Referral Code Submitted",
  REFERRAL_CODE_ACCEPTED: "Referral Code Accepted",
  REFERRAL_CODE_SUBMISSION_FAILED: "Referral Code Submission Failed",
  REFERRAL_CODE_SHARED: "Referral Code Shared",

  // Quest Events
  /**
   * A quest was finished, for any product and any quest type. Server-side off the
   * completion, so it is unaffected by whether the client was open to see it.
   *
   * Distinct from `CEDE_ARENA_QUEST_COMPLETED`, which is the bespoke onboarding quest's
   * own terminal event and fires on the ticket grant.
   */
  QUEST_COMPLETED: "Quest - Completed",
  QUEST_CLAIM_REWARDS_START: "Quest Claim Rewards - Start",
  QUEST_CLAIM_REWARDS_SUCCESS: "Quest Claim Rewards - Success",
  QUEST_CLAIM_REWARDS_ERROR: "Quest Claim Rewards - Error",
  // X Quests
  X_INTERACTION_QUEST_SUCCESS: "X Interaction Quest - Success",

  // TMA Onboarding Rewards Events
  CLAIM_TMA_ONBOARDING_REWARDS_SUCCESS: "Claim TMA Onboarding Rewards - Success",
  CLAIM_TMA_ONBOARDING_REWARDS_ERROR: "Claim TMA Onboarding Rewards - Error",
  TMA_UNAVAILABLE_KEEP_IN_TOUCH_CLICKED: "TMA Unavailable - Keep in Touch Clicked",
  TMA_UNAVAILABLE_VIEWED: "TMA Unavailable - Viewed",

  // Rewards Events
  CLAIM_REWARDS: "Claim Rewards",
  CLAIM_REFERRAL_REWARDS: "Claim Referral Rewards",
  CLAIM_CASHBACK_REWARDS: "Claim Cashback Rewards",

  // Cashback Events
  CASHBACK_DIALOG_OPENED: "Cashback - Dialog Opened",
  CASHBACK_DIALOG_CLOSED: "Cashback - Dialog Closed",
  CASHBACK_BUTTON_CLICKED: "Cashback - Button Clicked",
  CASHBACK_OPEN_FROM_NAVIGATE_BUTTONS: "Cashback - Open from Navigate Buttons",

  // Spinning Wheel Events
  SPINNING_WHEEL_SPIN_STARTED: "Spinning Wheel - Spin Started",
  SPINNING_WHEEL_SPIN_ERROR: "Spinning Wheel - Spin Error",
  SPINNING_WHEEL_SPIN_FAILED: "Spinning Wheel - Spin Failed",
  SPINNING_WHEEL_SPIN_BUTTON_CLICKED: "Spinning Wheel - Spin Button Clicked",
  SPINNING_WHEEL_RESULTS_COLLECTED: "Spinning Wheel - Results Collected",
  SPINNING_WHEEL_GET_MORE_TICKETS_CLICKED: "Spinning Wheel - Get More Tickets Clicked",
  SPINNING_WHEEL_DIALOG_CLOSED: "Spinning Wheel - Dialog Closed",
  SPINNING_WHEEL_HISTORY_VIEW_OPENED: "Spinning Wheel - History View Opened",
  SPINNING_WHEEL_HISTORY_BACK_CLICKED: "Spinning Wheel - History Back Clicked",
  SPINNING_WHEEL_VIEW_HISTORY_CLICKED: "Spinning Wheel - View History Clicked",
  SPINNING_WHEEL_REWARD_SHARED: "Spinning Wheel - Reward Shared",
  SPINNING_WHEEL_REWARD_NAVIGATION: "Spinning Wheel - Reward Navigation",
  SPINNING_WHEEL_REWARDS_DIALOG_CLOSED: "Spinning Wheel - Rewards Dialog Closed",

  // Lottery Events
  LOTTERY_JOIN_DISCORD_CLICK: "Lottery - Join Discord Click",

  // Hub Banner Events
  HUB_BANNER_PAGINATION_CLICKED: "Hub Banner - Pagination Clicked",
  HUB_BANNER_BANNER_CLICKED: "Hub Banner - Banner Clicked",
  HUB_BANNER_BANNER_VIEWED: "Hub Banner - Banner Viewed",

  // Navigation Sidebar Events
  NAVIGATION_SIDEBAR_TOGGLE: "Navigation Sidebar - Toggle",
  NAVIGATION_SIDEBAR_ITEM_CLICKED: "Navigation Sidebar - Item Clicked",
  NAVIGATION_SIDEBAR_CONNECT_WALLET_CLICKED: "Navigation Sidebar - Connect Wallet Clicked",
  NAVIGATION_SIDEBAR_ACCOUNT_BUTTON_CLICKED: "Navigation Sidebar - Account Button Clicked",
  NAVIGATION_SIDEBAR_ACCOUNT_DROPDOWN_ITEM_CLICKED: "Navigation Sidebar - Account Dropdown Item Clicked",

  // Header Events
  HEADER_REWARDS_MENU_OPENED: "Header - Rewards Menu Opened",
  HEADER_REWARDS_MENU_ITEM_CLICKED: "Header - Rewards Menu Item Clicked",
  HEADER_WALLET_MENU_OPENED: "Header - Wallet Menu Opened",
  HEADER_WALLET_MENU_ITEM_CLICKED: "Header - Wallet Menu Item Clicked",
  HEADER_WALLET_ADDRESS_COPIED: "Header - Wallet Address Copied",

  // Miscellaneous UI Events
  WHEEL_OPEN_FROM_HEADER: "Wheel - Open from Header",
  WHEEL_OPEN_FROM_NAVIGATE_BUTTONS: "Wheel - Open from Navigate Buttons",
  WHEEL_OPEN_FROM_PROFILE_REWARDS: "Wheel - Open from Profile Rewards",
  OPEN_EXTERNAL_DOC: "Open External Doc",
  REPORT_BOTS_BUTTON_CLICK: "Report Bots Button Click",
  SNACKBAR_ERROR_SUPPORT_CLICK: "Snackbar error - Support Click",
  STEPPER_MODAL_NEXT: "Stepper Modal - Next",
  STEPPER_MODAL_BACK: "Stepper Modal - Back",
  STEPPER_MODAL_CLOSE: "Stepper Modal - Close",
  API_DOWN_SHOWN: "API Down - Shown",
  API_DOWN_RECOVERED: "API Down - Recovered",
  API_DOWN_DISCORD_CLICKED: "API Down - Discord Clicked",
  API_DOWN_X_CLICKED: "API Down - X Clicked",
  API_DOWN_BANNER_DISMISSED: "API Down - Banner Dismissed",

  // User Events
  LOG_OUT: "Log Out",

  // Portfolio Events
  OPEN_DEPOSIT_MODAL: "Clicked Deposit Button to open deposit modal",
  GENERATE_DEPOSIT_ADDRESS: "Clicked Deposit Button to generate deposit address",
  TRANSFER_USDT_CLICKED: "Deposit Modal - Transfer USDT Clicked",
  TRANSFER_USDT_SUCCESS: "Deposit Modal - Transfer USDT Success",
  TRANSFER_USDT_FAILED: "Deposit Modal - Transfer USDT Failed",

  // Easter Egg Events
  CLICK_AND_EARN_MILESTONE: "Click and Earn - Milestone Reached",

  // Automatic Streak Events
  AUTOMATIC_STREAK_CGU_SIGNED: "Automatic Streak CGU Signed",
  AUTOMATIC_STREAK_CREATED: "Automatic Streak Created",
  AUTOMATIC_STREAK_STARTED: "Automatic Streak Started",
  AUTOMATIC_STREAK_CANCELLED: "Automatic Streak Cancelled",
  /** Unified event for streak completion/stop/failure */
  AUTOMATIC_STREAK_ENDED: "Automatic Streak Ended",
  /** Fired when a cycle is skipped due to spread threshold */
  AUTOMATIC_STREAK_CYCLE_SKIPPED: "Automatic Streak Cycle Skipped",
  /** Fired when a cycle is rescheduled (e.g., spread too high, will retry later) */
  AUTOMATIC_STREAK_CYCLE_RESCHEDULED: "Automatic Streak Cycle Rescheduled",

  // Feedback Modal Events
  FEEDBACK_MODAL_OPENED: "Feedback Modal - Opened",
  FEEDBACK_MODAL_SUBMITTED: "Feedback Modal - Submitted",

  // Airdrop Farming Events
  AIRDROP_FARMING_INTRO_VIEWED: "Airdrop Farming - Intro Viewed",
  AIRDROP_FARMING_PAGE_VIEWED: "Airdrop Farming - Page Viewed",
  AIRDROP_FARMING_BOT_CREATED: "Airdrop Farming - Bot Created",
  AIRDROP_FARMING_BOT_PAUSED: "Airdrop Farming - Bot Paused",
  AIRDROP_FARMING_BOT_FINALIZED: "Airdrop Farming - Bot Finalized",
  AIRDROP_FARMING_BOT_DUPLICATED: "Airdrop Farming - Bot Duplicated",
  AIRDROP_FARMING_CONTENT_VIEW_CHANGED: "Airdrop Farming - Content View Changed",
  AIRDROP_FARMING_STRATEGY_TAB_CHANGED: "Airdrop Farming - Strategy Tab Changed",
  AIRDROP_FARMING_BOT_STOP_REQUESTED: "Airdrop Farming - Bot Stop Requested",
  AIRDROP_FARMING_BOT_HIDDEN: "Airdrop Farming - Bot Hidden",
  AIRDROP_FARMING_LAUNCH_BOT_CLICKED: "Airdrop Farming - Launch Bot Clicked",
  AIRDROP_FARMING_FUNDING_RATES_ARBITRAGE_VIEWED: "Airdrop Farming - Funding Rates Arbitrage Viewed",
  AIRDROP_FARMING_PORTFOLIO_CLICKED: "Airdrop Farming - Portfolio Clicked",
  AIRDROP_FARMING_UNAVAILABLE_MARKET_CTA_CLICKED: "Airdrop Farming - Unavailable Market CTA Clicked",
  AIRDROP_FARMING_BLOCKING_POSITIONS_CLOSED: "Airdrop Farming - Blocking Positions Closed",
  AIRDROP_FARMING_BLOCKING_POSITIONS_CLOSE_ALL_CLICKED: "Airdrop Farming - Blocking Positions Close All Clicked",
  AIRDROP_FARMING_BLOCKING_POSITIONS_CLOSE_FAILED: "Airdrop Farming - Blocking Positions Close Failed",

  // Cede Arena Cross-Promo Events
  CEDE_ARENA_PROMO_BANNER_CLICKED: "Cede Arena Promo Banner - Clicked",

  // Exclusive Campaign Notification Events
  EXCLUSIVE_NOTIFICATION_BELL_CLICKED: "Exclusive Notification - Bell Clicked",
  EXCLUSIVE_NOTIFICATION_CAMPAIGN_CLICKED: "Exclusive Notification - Campaign Clicked",
  EXCLUSIVE_NOTIFICATION_CAMPAIGN_DISMISSED: "Exclusive Notification - Campaign Dismissed",
  EXCLUSIVE_NOTIFICATION_TOOLTIP_SHOWN: "Exclusive Notification - Tooltip Shown",

  // Restricted Campaign Access Events
  RESTRICTED_CAMPAIGN_PAGE_VIEWED: "Restricted Campaign - Page Viewed",
  RESTRICTED_CAMPAIGN_CONTACT_BD_CLICKED: "Restricted Campaign - Contact BD Clicked",
  RESTRICTED_CAMPAIGN_BACK_TO_CAMPAIGNS_CLICKED: "Restricted Campaign - Back to Campaigns Clicked",

  // CEX Affiliate Referral Campaign Events
  /** Fired when a user attempts to enter a referral-only campaign */
  REFERRAL_CAMPAIGN_ENTRY_ATTEMPT: "Referral Campaign - Entry Attempt",
  /** Fired when a user is blocked from a referral-only campaign because they are not affiliated */
  REFERRAL_CAMPAIGN_BLOCKED: "Referral Campaign - Blocked",
  /** Fired when a user is whitelisted (verified as affiliated) for a referral-only campaign */
  REFERRAL_CAMPAIGN_WHITELISTED: "Referral Campaign - Whitelisted",

  // Kraken Page Events
  VIEW_KRAKEN_PAGE: "View Kraken page",
  KRAKEN_SHARE_CLICKED: "Kraken - Share Clicked",
  KRAKEN_CREATE_ACCOUNT_CLICKED: "Kraken - Create Account Clicked",

  // Coinbase Page Events
  VIEW_COINBASE_PAGE: "View Coinbase page",
  COINBASE_SHARE_CLICKED: "Coinbase - Share Clicked",
  COINBASE_CREATE_ACCOUNT_CLICKED: "Coinbase - Create Account Clicked",

  // LBank Page Events
  VIEW_LBANK_PAGE: "View LBank page",
  LBANK_SHARE_CLICKED: "LBank - Share Clicked",
  LBANK_CREATE_ACCOUNT_CLICKED: "LBank - Create Account Clicked",

  // Flow Landing Events
  FLOW_LANDING_PAGE_VIEWED: "Flow - Landing Page Viewed",

  // Flow Leaderboard Events
  FLOW_LEADERBOARD_PAGE_VIEWED: "Flow - Leaderboard Page Viewed",

  // Cede Flow Trade & Earn Events
  CEDE_FLOW_TRADE_AND_EARN_PAGE_VIEWED: "Cede Flow Trade & Earn - Page Viewed",
  CEDE_FLOW_TRADE_AND_EARN_GROUP_POPOVER_OPENED: "Cede Flow Trade & Earn - Group Popover Opened",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_VIEWED: "Cede Flow Trade & Earn - Competition Viewed",
  CEDE_FLOW_TRADE_AND_EARN_LAUNCH_BOT_CLICKED: "Cede Flow Trade & Earn - Launch Bot Clicked",
  CEDE_FLOW_TRADE_AND_EARN_REWARDS_TABLE_OPENED: "Cede Flow Trade & Earn - Rewards Table Opened",
  CEDE_FLOW_TRADE_AND_EARN_VIEW_BOTS_CLICKED: "Cede Flow Trade & Earn - View Bots Clicked",

  // Cross-App Redirect Events
  CROSS_APP_REDIRECT_TO_FLOW: "Cross App - Redirect to Flow",
  CROSS_APP_REDIRECT_TO_ARENA: "Cross App - Redirect to Arena",
  CROSS_APP_REDIRECT_TO_HUB: "Cross App - Redirect to Hub",

  // Big Arena Competition whitelist (`/100k`)
  BIG_ARENA_COMPETITION_PAGE_VIEWED: "Big Arena Competition - Page Viewed",
  /** The registration request landed; `status` says whether a priority code accepted it. */
  BIG_ARENA_COMPETITION_REGISTRATION_SUBMITTED: "Big Arena Competition - Registration Submitted",
  BIG_ARENA_COMPETITION_SHARED: "Big Arena Competition - Shared",
  /** The duels-lobby promo banner. Pairs with the page view's `utm` to close the funnel. */
  BIG_ARENA_COMPETITION_BANNER_CLICKED: "Big Arena Competition - Banner Clicked",
  /** The registered pass's "Enter the arena" CTA; `has_bonus` says whether the starter quest was on offer. */
  BIG_ARENA_COMPETITION_TRY_ARENA_CLICKED: "Big Arena Competition - Try Arena Clicked",
  /** The pass's wallet "Connect" button; pairs with Wallet Connected for the drop-off in the prompt. */
  BIG_ARENA_COMPETITION_WALLET_CONNECT_CLICKED: "Big Arena Competition - Wallet Connect Clicked",
  /** The backend stored the wallet on the account, not merely the Privy prompt closing. */
  BIG_ARENA_COMPETITION_WALLET_CONNECTED: "Big Arena Competition - Wallet Connected",

  // Tournament landing (`/100k-live`)
  /** Auto-fires once on the /100k-live tournament landing page; `has_active_tournament` + `tournament_status` frame the funnel. */
  TOURNAMENT_LANDING_PAGE_VIEWED: "Tournament Landing - Page Viewed",
  /** A CTA click on /100k-live; `location` is "hero" | "live_stats", pairs with the page view to close the funnel to joins. */
  TOURNAMENT_LANDING_CTA_CLICKED: "Tournament Landing - CTA Clicked",

  // Cede Arena Events
  CEDE_ARENA_PAGE_VIEWED: "Cede Arena - Page Viewed",
  CEDE_ARENA_USERNAME_PROMPT_OPENED: "Cede Arena - Username Prompt Opened",
  CEDE_ARENA_USERNAME_SET: "Cede Arena - Username Set",
  /** Fired when the user confirms joining a scheduled competition (the opt-in dialog's confirm), before funding routes it. */
  CEDE_ARENA_OPT_IN_CLICKED: "Cede Arena - Opt-In Clicked",
  /** Fired when the user clicks the "Pay X USDT" CTA on an AWAITING_PAYMENT entry. */
  CEDE_ARENA_PAY_CLICKED: "Cede Arena - Pay Clicked",
  /** Fired when the Instant-Matchmaking onboarding modal opens. `entry_step` reports which step the user lands on. */
  CEDE_ARENA_ONBOARDING_OPENED: "Cede Arena - Onboarding Opened",
  /** Fired each time a step is satisfied while the onboarding modal is open. */
  CEDE_ARENA_ONBOARDING_STEP_COMPLETED: "Cede Arena - Onboarding Step Completed",
  /** Fired when the onboarding modal closes before reaching opt-in. */
  CEDE_ARENA_ONBOARDING_ABANDONED: "Cede Arena - Onboarding Abandoned",
  /** Fired when the user claims their matured Arena withdrawal(s) to their wallet. */
  CEDE_ARENA_WITHDRAW_CLAIMED: "Cede Arena - Withdraw Claimed",
  /** Fired when the user opts out of a competition they had joined (releases the locked fee). */
  CEDE_ARENA_CAMPAIGN_OPT_OUT: "Cede Arena - Campaign Opt-Out",
  /** Fired once per competition when it starts, including private rooms, with the frozen participant set, fees, and prize pool. */
  CEDE_ARENA_COMPETITION_STARTED: "Cede Arena - Competition Started",
  /** Fired once per Spin & Go competition when its pot factor is claimed at lock — carries the drawn factor, collected buy-ins, resized pool, and house delta. */
  CEDE_ARENA_SPIN_RESULT: "Cede Arena - Spin Result",
  /** Fired after an Arena participant successfully joins and any entry fee is locked. */
  CEDE_ARENA_COMPETITION_OPTED_IN: "Cede Arena - Competition Opted In",
  /** Fired once per participant after an Arena competition's final leaderboard is committed. */
  CEDE_ARENA_COMPETITION_FINISHED: "Cede Arena - Competition Finished",
  /** Fired for the responsible user when a proposed match does not proceed — they declined or ignored (ready-check timeout). */
  CEDE_ARENA_MATCH_NOT_PROCEEDED: "Cede Arena - Match Not Proceeded",

  // Cede Arena standalone-app analytics (separate from the in-Hub CEDE_ARENA_* events)
  ARENA_PAGE_VIEWED: "Arena - Page Viewed",
  ARENA_WALLET_CONNECT_CLICKED: "Arena - Wallet Connect Clicked",
  ARENA_WALLET_CONNECTED: "Arena - Wallet Connected",
  ARENA_WALLET_DISCONNECTED: "Arena - Wallet Disconnected",
  /** Fired on every user click that enters a duel (lobby mode card, tournament duel, post-battle next step), before sign-in or funding is checked. `tier` is the one picked. */
  ARENA_QUICK_MATCH_CLICKED: "Arena - Quick Match Clicked",
  /** Fired when the user clicks the Private destination in the header nav (mode selector or mobile menu). */
  ARENA_PRIVATE_COMPETITION_CLICKED: "Arena - Private Competition Clicked",
  /** The share editor opened on a settled result. `moment` (duel_win | spin_win | royale_win | room_win | place | best_moment), `mode`, `isPrompt` (opened from a Share showing the one strong-moment prompt). */
  ARENA_SHARE_OPENED: "Arena - Share Opened",
  /** One option changed in the share editor. `field` is show (result | comeback | clutch-escape | big-close) | style | art | background | caption | format | size (the video's 16:9 / 9:16), `value` the new pick, `format` image | video at the time. */
  ARENA_SHARE_CUSTOMIZED: "Arena - Share Customized",
  /** A share action finished. `channel` share | copy | save | x (x: the post intent opened; the post itself is not observable), `format` image | video, `durationMs` (video only: the MP4's length), plus `moment`, `show`, `style`, `art`, `background`, `caption`, `hasRef`. */
  ARENA_SHARE_COMPLETED: "Arena - Share Completed",
  /**
   * Outcome of the ready check once an opponent is found. `status` is
   * "accepted" (pressed I'm ready), "declined" (pressed Decline) or "expired"
   * (the deadline ran out). One event rather than three: it is only ever read
   * as the accept rate, which a property splits just as well.
   */
  ARENA_MATCH_READY_CHECK: "Arena - Match Ready Check",
  /** Fired when the user clicks "Find match". `status` is "success" if the opt-in queue was joined, or "not_enough_credit" if the virtual balance was insufficient. */
  ARENA_FIND_MATCH_CLICKED: "Arena - Find Match Clicked",
  /** Fired when the user clicks the Free (Casual) tier tile in the stake picker. Carries `source: "feed_match"` when started from a Feed join's Match. */
  ARENA_FREE_MATCHMAKING_CLICKED: "Arena - Free Matchmaking Clicked",
  /** Fired when the user clicks the Ranked (paid) tier tile in the stake picker. Carries `source: "feed_match"` when started from a Feed join's Match. */
  ARENA_RANKED_MATCHMAKING_CLICKED: "Arena - Ranked Matchmaking Clicked",
  /** Fired when the user clicks the Spin & Go tier tile in the stake picker. Carries `source: "feed_match"` when started from a Feed join's Match, `"post_practice"` from the post-practice pitch. */
  ARENA_SPIN_AND_GO_MATCHMAKING_CLICKED: "Arena - Spin & Go Matchmaking Clicked",
  /** Fired client-side when a Spin & Go wheel finishes landing and reveals the drawn multiplier + resized pool. `surface` names where it fired (quick_match | private_room | opt_in). */
  ARENA_SPIN_AND_GO_WHEEL_REVEALED: "Arena - Spin & Go Wheel Revealed",
  /** Fired when the user opens Arena funding from any deposit entry point. */
  ARENA_DEPOSIT_CLICKED: "Arena - Deposit Clicked",
  /** Fired after a deposit is credited to the Arena virtual-balance ledger. */
  ARENA_DEPOSIT_CREDITED: "Arena - Deposit Credited",
  /**
   * One step of Unifold's in-modal deposit journey, with the step name in
   * `step` (`deposit.method_selected`, `deposit.wallet_connection_failed`, …).
   * A single event rather than one per step: there are 19 of them, and they are
   * only ever read as a funnel, which filters on a property just as well.
   * Carries the full payload, so `id` (the execution id) joins a client journey
   * to the settlement webhook. Never a source of truth for money — that is the
   * webhook itself.
   *
   * `deposit_method` is named to match {@link ARENA_DEPOSIT_CREDITED}, so one
   * filter slices the whole deposit funnel. Amounts are NOT unified: `amountUsd`
   * here is what the user asked to pay, `amount_usdt` there is what settled.
   */
  ARENA_DEPOSIT_FLOW_STEP: "Arena - Deposit Flow Step",
  /** Fired when the user clicks the "User guide" link. */
  ARENA_USER_GUIDE_CLICKED: "Arena - User Guide Clicked",
  /** Fired when the user clicks the "Join Discord" link. */
  ARENA_JOIN_DISCORD_CLICKED: "Arena - Join Discord Clicked",
  /** Fired when the user clicks the "Follow on X" link. */
  ARENA_FOLLOW_X_CLICKED: "Arena - Follow X Clicked",
  /** Fired when the user clicks "Contact support" (routes to the Discord server). */
  ARENA_SUPPORT_CLICKED: "Arena - Support Clicked",
  /** Mobile header: the Mode chip's panel opened. Property: trigger (`tap` | `keyboard`; `hold` and `caret` came from the retired bar centre). */
  ARENA_PLACE_CHOOSER_OPENED: "Arena - Place Chooser Opened",
  /** A mode picked in the header: the mobile Mode chip's panel, or a row of the `lg+` Play launcher (`br_home_enable`). Property: place (`play` | `battle_royale` | `private`, plus `spin` and `free` from the launcher). */
  ARENA_PLACE_CHOSEN: "Arena - Place Chosen",
  /** Fired when the user clicks the "Withdraw" button on the portfolio page. */
  ARENA_WITHDRAW_CLICKED: "Arena - Withdraw Clicked",
  /** Fired when the user clicks the "Claim" button on the portfolio page (opens the claim modal). */
  ARENA_CLAIM_CLICKED: "Arena - Claim Clicked",
  /** Fired when the user clicks the in-modal claim button to submit the on-chain claim transaction. */
  ARENA_CLAIM_SUBMITTED: "Arena - Claim Submitted",
  /** Fired when the user clicks "Remind me" on a scheduled session. The reminder itself is not built yet — this measures demand for it. `competitionName` is the campaign slug. */
  ARENA_SESSION_REMINDER_REQUESTED: "Arena - Session Reminder Requested",
  /** Fired when the Arena portfolio page is viewed. */
  ARENA_PORTFOLIO_PAGE_VIEWED: "Arena - Portfolio Page Viewed",
  /** Fired when the Arena profile page is viewed (after the flag gate). */
  ARENA_PROFILE_PAGE_VIEWED: "Arena - Profile Page Viewed",

  // Arena referrals. Server-side events are the truth of record for money and
  // attribution; the copy/view events are client-side intent.
  /** Referrer crossed the qualifying-volume threshold and their code became available. Props: `referral_code`, `qualifying_volume_usdt`. */
  ARENA_REFERRAL_CODE_UNLOCKED: "Arena - Referral Code Unlocked",
  /** A referral attribution row was written. Props: `referrer_hub_user_id`, `referral_code`, `referral_source` (`link` | `manual`). */
  ARENA_USER_REFERRED: "Arena - User Referred",
  /**
   * A referee completed the referral bonus quest and earned its ticket pack. Props:
   * `referrer_hub_user_id`, `quest_id`, `ticket_pack_value_usdt`.
   *
   * Not emitted yet: the quest that would fire it has no completion hook wired. Land
   * that alongside the emit rather than leaving the funnel with a gap here.
   */
  ARENA_REFERRAL_BONUS_QUEST_COMPLETED: "Arena - Referral Bonus Quest Completed",
  /**
   * Referral rewards were claimed into the virtual balance. Fired after the credit
   * lands, not on the button: a click also fires for no-op and failed claims, and
   * misses a credit completed later by the repair path. Props: `amount_usdt`,
   * `reward_count`, `claim_batch_id`.
   */
  ARENA_REFERRAL_REWARDS_CLAIMED: "Arena - Referral Rewards Claimed",
  /** Fired when the Arena referrals page is viewed. Props: `isUnlocked`, `referredCount`. */
  ARENA_REFERRALS_PAGE_VIEWED: "Arena - Referrals Page Viewed",
  /** Copy-code action on the referrals page. Kept separate from the link copy: which one people reach for is what tells you whether the link plumbing earned its cost. */
  ARENA_REFERRAL_CODE_COPIED: "Arena - Referral Code Copied",
  /** Copy-link action on the referrals page. */
  ARENA_REFERRAL_LINK_COPIED: "Arena - Referral Link Copied",
  /**
   * Fired when the Arena clans page is viewed. Props: `clanCount`, `isMember`, `loadFailed`. Fires
   * once the reads settle, so a visit counts even when one fails; the property it would have
   * described is null there rather than a zero that never happened.
   */
  ARENA_CLANS_PAGE_VIEWED: "Arena - Clans Page Viewed",
  /**
   * A clan's detail panel was shown. One event per clan reached, not per click: a deep link and
   * the member's own clan open it without one. Props: `clanSlug`, `isOwnClan`, `source`
   * (`pick | deep_link | default`).
   */
  ARENA_CLAN_DETAIL_VIEWED: "Arena - Clan Detail Viewed",
  /**
   * The Create clan button was pressed while creation is still ops-only: the demand signal for
   * building it. Props: `isMember`. Read it as unique users: the button carries a "Coming soon"
   * tip, and on touch the tap that dismisses the tip presses the button again.
   */
  ARENA_CLAN_CREATE_CLICKED: "Arena - Clan Create Clicked",
  /**
   * The viewer joined a clan. Props: `clanId`, `clanSlug`, `isPrivate`, and `source`
   * (`browse | password | invite_link`) — `isPrivate` cannot stand in for it, since a private
   * clan joined from a link is private too.
   */
  ARENA_CLAN_JOINED: "Arena - Clan Joined",
  /** The viewer left their clan. Props: `clanId`, `clanSlug`. */
  ARENA_CLAN_LEFT: "Arena - Clan Left",
  /** A member copied their clan's invite link; on a private clan only the chief can. Props: `clanId`, `clanSlug`, `isPrivate`. */
  ARENA_CLAN_INVITE_LINK_COPIED: "Arena - Clan Invite Link Copied",
  /** A chief copied their private clan's join code. Props: `clanId`, `clanSlug`. */
  ARENA_CLAN_INVITE_CODE_COPIED: "Arena - Clan Invite Code Copied",
  /** An invite link was opened. Fires before sign-in, so it is the top of the invite funnel. Props: `clanSlug`, `isAuthed`. */
  ARENA_CLAN_INVITE_OPENED: "Arena - Clan Invite Opened",
  /**
   * An invite link's offer resolved. Props: `clanSlug`, `outcome` (`joined | blocked | failed`), and
   * for a non-join a machine-readable `reason`, never the displayed sentence, which carries a clan
   * name. `blocked` is refused before the call (`link_dead | already_in_this_clan | in_another_clan |
   * cooldown | clan_full | viewer_load_failed`); `failed` is the API refusing one the dialog offered,
   * so it is the race bucket (`link_dead | wrong_password | in_another_clan | cooldown | clan_full |
   * request_failed`).
   */
  ARENA_CLAN_INVITE_RESOLVED: "Arena - Clan Invite Resolved",
  /** Fired when the user views a trading competition page (replaces HYPERLIQUID_TAB_VIEWED for Arena). `competitionName` is the campaign slug. */
  ARENA_TRADING_COMPETITION_VIEWED: "Arena - Trading Competition Viewed",
  /**
   * One of the battle screen's on-demand surfaces was opened. `surface` is
   * `price_chart | pnl_chart | standing_detail | market | triggers | order_form`;
   * `layout` is `mobile | desktop`, `tradeMode` is `simple | advanced`.
   *
   * `price_chart` is the one impression rather than an intent: the TradingView
   * chart is on screen by default on both layouts, so it fires once per mount
   * when the chart is first both ready and visible. It exists to be the shared
   * denominator of {@link ARENA_CHART_REFRAMED}; read it with the other
   * values only after splitting on `surface`.
   *
   * `order_form` additionally carries `source` (`action_bar | chart | tour`)
   * and `side`; `standing_detail` carries `control` (`chip | lead_bar`), since
   * the lead bar is a second way into the same panel and only a split tells you
   * whether it earns its place. The tour opens the drawer FOR the trader, so that source has to
   * stay separable — counting it as intent would inflate the one funnel step
   * the mobile onboarding is judged on.
   *
   * One event rather than four: it is only ever read as "which of these do
   * traders actually use", which a property splits just as well — the same
   * shape `ARENA_MATCH_READY_CHECK` uses for its outcomes.
   *
   * It exists because the mobile chrome moved these behind chips, and nothing
   * measured whether the PnL chart was worth its place.
   *
   * `pnl_chart` was mobile-only until desktop's PnL Evolution tab started
   * firing it, so any series crossing that release must split on `layout`.
   *
   * `format` is `battle_royale | classic`. Events sent before Battle Royale shipped
   * carry no `format`; read them as `classic`.
   */
  ARENA_BATTLE_SURFACE_OPENED: "Arena - Battle Surface Opened",
  /**
   * A Battle Royale elimination landed while the battle page was open. One event per
   * elimination per page; eliminations made before the page mounted send none, so
   * a viewer arriving late reports only what they watched. Props: `competitionName`,
   * `eliminationIndex` (1-based), `eliminatedCount`, `eliminationMode` (ONE, COUNT,
   * PERCENT, SCHEDULE, ROUNDS), `skipped` (only games recorded before elimination modes).
   */
  ARENA_BR_ELIMINATION: "Arena - BR Elimination",
  /**
   * The viewer was knocked out of a Battle Royale, seen live on the battle page.
   * Props: `competitionName`, `place` (the top of a shared place: 5 for "#5–6"),
   * `eliminationIndex`, `hadTraded` (false for the idle sweep at elimination 1), `eliminationMode`.
   */
  ARENA_BR_ELIMINATED: "Arena - BR Eliminated",
  /**
   * An eliminated Battle Royale player was still on the battle page when it ended.
   * Read against {@link ARENA_BR_ELIMINATED} for the share who stay to watch. It
   * excludes the runner-up, whose final cut lands at the end with nothing left to
   * watch, and counts players who came back after their elimination. Props:
   * `competitionName`, `place`.
   */
  ARENA_BR_WATCHED_TO_END: "Arena - BR Watched To End",
  /**
   * The price chart's visible range was expanded to bring an order or position
   * line into frame. `trigger` is `position | limit | take_profit | stop_loss`,
   * `layout` is `mobile | desktop`.
   *
   * One event per re-frame, not per gesture: lines that land together (a
   * position with its exits) coalesce into one, attributed to the position. A
   * line whose price moves can re-frame again, so this is far below gesture
   * volume but not a hard per-order bound.
   *
   * `wasAdjustedByUser` is the signal that the framing was wrong: the trader
   * changed the viewport themselves within the settle window that follows.
   * `secondsUntilAdjust` times that correction and is null when there was none.
   *
   * Read against `ARENA_BATTLE_SURFACE_OPENED { surface: "price_chart" }`,
   * which is the per-mount denominator.
   */
  ARENA_CHART_REFRAMED: "Arena - Chart Reframed",
  /**
   * A position's TP/SL was successfully saved. `hasTakeProfit` / `hasStopLoss`
   * describe the SAVED state, so clearing both is `action: "cleared"`.
   *
   * Pairs with `ARENA_BATTLE_SURFACE_OPENED { surface: "triggers" }` to read as
   * a two-step funnel: opened the editor → committed a change.
   */
  ARENA_POSITION_TRIGGERS_SAVED: "Arena - Position Triggers Saved",
  /**
   * The trader changed one field of their simple-mode look and sound. One event per changed
   * field: `{ setting, from, to }`. Nothing fires when the value does not change.
   */
  ARENA_TRADE_STYLE_CHANGED: "Arena - Trade Style Changed",
  /**
   * The battle page's order ticket changed mode: `{ from, to, via }`, `simple | advanced`. `via: "switch"` is the
   * trader's own pick; `via: "default"` fires once per user and browser when the first-visit default is saved
   * (`from: null`). Leaving advanced with resting limit orders asks first; that switch adds `{ restingOrders,
   * cancelled }`, and fires only once the trader answered it.
   */
  ARENA_TRADE_MODE_SWITCHED: "Arena - Trade Mode Switched",
  /**
   * A seated participant saw their competition live, once per page mount: `{ campaignName, tradeMode, layout,
   * secondsSinceStart }`. The anchor for time to first fill (first `Trade Executed` minus this); a late or
   * reloaded page shows in `secondsSinceStart`. The practice duel does not fire it.
   */
  ARENA_DUEL_LIVE_VIEWED: "Arena - Duel Live Viewed",
  /**
   * One per paper order on the battle page (ticket, simple bet or positions-table close), success or rejection:
   * `{ campaignName, playerCount, tradeMode, layout, orderType, reduceOnly, ok, errorType, inputDelayMs, tapToPaintMs,
   * tapToSendMs, sendToResponseMs, responseToPaintMs }`. Clocks start when the submit handler runs; `inputDelayMs` is
   * the wait before it, the tap held by a busy main thread. `tapToSendMs` is client work before the request, plus the
   * Privy token refresh when the cached token has expired (a network call), `sendToResponseMs` network + server,
   * `responseToPaintMs` the main thread from the order settling to the next paint; `tapToPaintMs` runs from the handler
   * to the first paint of the applied response (positions, account), so what the three miss (parsing, the cache write)
   * is the remainder. A resting limit order's Open Orders row comes later, from a refetch. `playerCount` is the roster
   * size, eliminated players included. `inputDelayMs` is null without Event Timing (Safari) or when the tap's event
   * took under its 16 ms floor; request fields are null when the browser recorded no timing or another order
   * overlapped; paint fields are null in a hidden tab or after 10 s without a frame. `errorType` is the RFC 9457 `type`
   * of a rejection, the class name of any other Error, and null otherwise.
   */
  ARENA_PAPER_ORDER_TIMING: "Arena - Paper Order Timing",
  /**
   * Once per visible minute while a seated player watches a live battle, plus the partial window when that stops or
   * the screen changes, Chromium only (`long-animation-frame`): `{ campaignName, playerCount, layout, tradeMode,
   * eliminationReason, windowMs, longFrames, freezes, worstFrameMs, blockedMs, worstFrameScript }`. Whether the page itself
   * freezes: `freezes` counts frames of 200 ms or more (what a player notices), `longFrames` every frame over the
   * browser's 50 ms floor. Divide `blockedMs` by `windowMs` to compare windows; `worstFrameScript` names the script
   * that ran longest in the worst frame. `eliminationReason` is null for a player still in; BATTLE_ROYALE_ELIMINATED
   * shows the out screen, DRAWDOWN_ELIMINATED keeps the trading page with orders disabled.
   */
  ARENA_BATTLE_LONG_FRAMES: "Arena - Battle Long Frames",
  // ── Friend graph (DEV-708) ────────────────────────────────────────────────
  /**
   * A friend request was sent. `entryPoint` names the add-path (post_duel | search |
   * leaderboard | profile_card) — the split that says which entry point actually
   * grows the graph. The invite link never sends a request, it emits
   * ARENA_FRIEND_INVITE_CLAIMED.
   */
  ARENA_FRIEND_REQUEST_SENT: "Arena - Friend Request Sent",
  /**
   * A request was accepted, by whoever's tap made the friendship: the addressee
   * answering it, or a sender whose request met one already waiting from the
   * other side (the server accepts that instead of sending). `entryPoint` is the
   * surface tapped: friends_panel, or any SENT source. Not a census of who gained
   * a friend: the requester and the inviter whose link was claimed emit nothing.
   * Build the has-a-friend cohort from this OR ARENA_FRIEND_INVITE_CLAIMED OR
   * ARENA_FRIENDS_PANEL_OPENED with friendCount >= 1.
   */
  ARENA_FRIEND_REQUEST_ACCEPTED: "Arena - Friend Request Accepted",
  /** `entryPoint` is always friends_panel, the one surface with a decline. */
  ARENA_FRIEND_REQUEST_DECLINED: "Arena - Friend Request Declined",
  ARENA_FRIEND_REMOVED: "Arena - Friend Removed",
  /** The Friends dropdown was opened. Props: `pendingCount`, `friendCount`. */
  ARENA_FRIENDS_PANEL_OPENED: "Arena - Friends Panel Opened",
  /** An invite link was minted. Pairs with ARENA_FRIEND_INVITE_CLAIMED to read as a funnel. */
  ARENA_FRIEND_INVITE_CREATED: "Arena - Friend Invite Created",
  ARENA_FRIEND_INVITE_CLAIMED: "Arena - Friend Invite Claimed",
  /** Fired once when the general-leaderboard page mounts (after the flag gate). Props: initial `scope` + `window`. */
  ARENA_LEADERBOARD_VIEWED: "Arena - Leaderboard Viewed",
  /** Fired when the user clicks a general-leaderboard entry point. `source` names the origin (home_chip | home_link | nav). */
  ARENA_LEADERBOARD_ENTRY_CLICKED: "Arena - Leaderboard Entry Clicked",
  /** Fired when the user switches the leaderboard scope tab. `scope` is the new value. */
  ARENA_LEADERBOARD_SCOPE_CHANGED: "Arena - Leaderboard Scope Changed",
  /** Fired when the user switches the leaderboard time window. `window` is the new value. */
  ARENA_LEADERBOARD_WINDOW_CHANGED: "Arena - Leaderboard Window Changed",
  /** Fired once when the weekly-leaderboard view mounts (after the flag gate). Props: initial `buyInUsdt` + `weekOffset`. */
  ARENA_WEEKLY_LEADERBOARD_VIEWED: "Arena - Weekly Leaderboard Viewed",
  /**
   * Fired when a player profile card has been open for 1s — the dwell threshold is
   * what separates a real read from a mouse sweep across a leaderboard. At most once
   * per viewed player per session. Props: `viewedHubUserId`, `surface`, `isSelf`,
   * plus the card's own contents (`crownCount`, `wins`, `bestWinStreak`,
   * `hasSocials`) so retention can be correlated with what was actually on show.
   */
  ARENA_PLAYER_PROFILE_VIEWED: "Arena - Player Profile Viewed",
  /** The viewer uploaded a new profile picture. */
  ARENA_PROFILE_AVATAR_UPDATED: "Arena - Profile Avatar Updated",
  /** The viewer removed their profile picture. */
  ARENA_PROFILE_AVATAR_REMOVED: "Arena - Profile Avatar Removed",
  /** The viewer saved or cleared their profile quote. Props: `cleared`, `length`. */
  ARENA_PROFILE_QUOTE_UPDATED: "Arena - Profile Quote Updated",
  /** Fired when the user switches the weekly leaderboard's buy-in board — not on re-picking the current one. `buyInUsdt` is the new board, `source` the surface (lobby_card | leaderboard_page). */
  ARENA_WEEKLY_LEADERBOARD_TIER_CHANGED: "Arena - Weekly Leaderboard Tier Changed",
  /** Fired when the user clicks into the weekly leaderboard. `source` names the origin (lobby_card | switcher). */
  ARENA_WEEKLY_LEADERBOARD_ENTRY_CLICKED: "Arena - Weekly Leaderboard Entry Clicked",
  /** Fired when the guided first-time-user experience starts (the lobby tour is first shown). */
  CEDE_ARENA_FTUE_STARTED: "Cede Arena - FTUE Started",
  /** Fired each time a guided step advances. `step` is the step id. */
  CEDE_ARENA_FTUE_STEP_COMPLETED: "Cede Arena - FTUE Step Completed",
  /** Fired when the user skips the guided onboarding OUTRIGHT — the SKIPPED mutation, terminal server-side. `at_step` names the home/lobby step. */
  CEDE_ARENA_FTUE_SKIPPED: "Cede Arena - FTUE Skipped",
  /**
   * Fired when the user hides the in-battle tour card ("Hide tips"). Distinct
   * from FTUE_SKIPPED on purpose: onboarding stays active, so these users still
   * reach FIRST_ORDER_PLACED / COMPLETED and belong in the activation
   * denominator. `at_step` is the battle step they were on (a T or M label).
   */
  CEDE_ARENA_FTUE_TOUR_DISMISSED: "Cede Arena - FTUE Tour Dismissed",
  /**
   * The "place your first trade" pill appeared. `via` says which path armed it:
   * `tour_handoff` (the mobile tour stepped aside for them) or `no_order_timer`
   * (they never reached that step and still had not traded). Denominator for
   * the two below.
   */
  CEDE_ARENA_FTUE_ORDER_NUDGE_SHOWN: "Cede Arena - FTUE Order Nudge Shown",
  /** A first paper order landed while the pill was up — the conversion the pill exists for. */
  CEDE_ARENA_FTUE_ORDER_NUDGE_CONVERTED: "Cede Arena - FTUE Order Nudge Converted",
  /** Fired on the user's first successful paper order during onboarding. */
  CEDE_ARENA_FTUE_FIRST_ORDER_PLACED: "Cede Arena - FTUE First Order Placed",
  /** The practice duel went live (its tape and mid loaded). `pair` is the market. Once per duel. */
  CEDE_ARENA_FTUE_PRACTICE_STARTED: "Cede Arena - FTUE Practice Started",
  /** The practice duel couldn't start. `pair` (null before one is chosen) and `reason`. Once per failure. */
  CEDE_ARENA_FTUE_PRACTICE_FAILED: "Cede Arena - FTUE Practice Failed",
  /**
   * Fired when the practice duel ends (it is always won). `pnl_sign` is how the
   * trader's own PnL finished (`positive` | `negative` | `zero`), `pair` the
   * market played and `accelerated` whether they fast-forwarded the end.
   */
  CEDE_ARENA_FTUE_COMPLETED: "Cede Arena - FTUE Completed",
  /** The trader fast-forwarded the practice duel. `pair` and `remaining_sec` (tape seconds left) say where. */
  CEDE_ARENA_FTUE_ACCELERATE_CLICKED: "Cede Arena - FTUE Accelerate Clicked",
  /** The lobby alerts offer, once per device. `outcome` is `enabled` | `failed` | `dismissed`. */
  CEDE_ARENA_ALERTS_PROMPTED: "Cede Arena - Alerts Prompted",
  /**
   * The device match-alert switch was driven by the user. `action` is `enable` |
   * `disable`, `source` names the surface that drove it, and `outcome` is
   * `succeeded` | `permission_denied` (enable only, `permission` says whether
   * they blocked us or swiped the sheet away) | `failed`. The silent mount-time
   * re-subscribe is NOT a decision and never fires this.
   */
  CEDE_ARENA_ALERTS_TOGGLED: "Cede Arena - Alerts Toggled",
  /** Fired when the user clicks a completion-card CTA (ranked battle / browse competitions). */
  CEDE_ARENA_FTUE_CTA_CLICKED: "Cede Arena - FTUE CTA Clicked",
  /**
   * Fired once, when a user is first enrolled into an experiment. The variant is
   * also set as a profile property; this event is what dates the enrolment, so a
   * funnel can be cut to users who were already bucketed when they hit it.
   */
  EXPERIMENT_ASSIGNED: "Experiment Assigned",
  /**
   * The pre-sign-in funnel. These fire on a DEVICE id (anonymous), so they are
   * the only view of everything before an account exists — without them the
   * funnel starts at sign-in and every drop-off before it is invisible.
   */
  CEDE_ARENA_LANDING_VIEWED: "Cede Arena - Landing Viewed",
  /** The landing's own CTA into the app. */
  CEDE_ARENA_LANDING_CTA_CLICKED: "Cede Arena - Landing CTA Clicked",
  /** The welcome takeover shown to a signed-out visitor. `has_offer` + `offer_*` describe the pack advertised. */
  CEDE_ARENA_WELCOME_SHOWN: "Cede Arena - Welcome Shown",
  /**
   * How the welcome ended: `sign_in` or `dismissed`. `referral_code_source` is `link`,
   * `manual` or `none` — `manual` is the modal's own code field, so impressions against
   * `manual` is what says whether asking there recovers attribution.
   */
  CEDE_ARENA_WELCOME_CLOSED: "Cede Arena - Welcome Closed",
  /** The iOS Add-to-Home-Screen tutorial. `trigger` is `auto` (first eligible visit) or `manual` (header button). */
  CEDE_ARENA_INSTALL_GUIDE_SHOWN: "Cede Arena - Install Guide Shown",
  /** How the install tutorial ended: `acknowledged` or `dismissed`. */
  CEDE_ARENA_INSTALL_GUIDE_CLOSED: "Cede Arena - Install Guide Closed",
  /**
   * Arena reached the Home Screen, once per device. `detected_via` names the
   * only signal that browser gives: `appinstalled` (Chromium), `standalone_launch`
   * (Safari), or `start_url` (Firefox, which offers neither of the other two).
   */
  CEDE_ARENA_PWA_INSTALLED: "Cede Arena - PWA Installed",
  /**
   * A push endpoint the push service reported dead (404/410), pruned by the API.
   * Read as "this device became unreachable": cleared site data and evicted
   * storage produce the same 410, so it is never an uninstall count.
   */
  CEDE_ARENA_PUSH_SUBSCRIPTION_LOST: "Cede Arena - Push Subscription Lost",
  /**
   * One recipient a transport accepted, emitted by the API per user reached.
   * `transport` is `in_app` or `web_push` — presence routes each user to exactly
   * one, so the pair splits rather than double-counts. Reached is not read: it
   * says the channel took it, never that anyone saw it.
   */
  CEDE_ARENA_NOTIFICATION_SENT: "Cede Arena - Notification Sent",
  /** The starter-pack reveal, once per account. `ticket_count` / `total_value_usdt` state what landed. */
  CEDE_ARENA_STARTER_PACK_SHOWN: "Cede Arena - Starter Pack Shown",
  /** How the reveal ended: `enter` or `dismissed`. */
  CEDE_ARENA_STARTER_PACK_CLOSED: "Cede Arena - Starter Pack Closed",
  /** A free-entry ticket was spent on an entry. The only Mixpanel view of redemption. */
  CEDE_ARENA_TICKET_SPENT: "Cede Arena - Ticket Spent",
  /**
   * The partner ticket-link landing at `/i/<slug>`. Anonymous (device id) like
   * the rest of the pre-sign-in funnel, and the only per-partner view of how
   * many people a KOL link actually put in front of the offer. `status` reports
   * what the visitor was shown (`ACTIVE` / `DISABLED` / `EXHAUSTED` / `unknown`).
   */
  CEDE_ARENA_TICKET_LINK_VIEWED: "Cede Arena - Ticket Link Viewed",
  /** The partner landing's CTA. `cta` names which one: `sign_in` or `browse_duels`. */
  CEDE_ARENA_TICKET_LINK_CTA_CLICKED: "Cede Arena - Ticket Link CTA Clicked",
  /**
   * A partner link actually paid out. Emitted by the API on the claim that
   * minted, so it is the qualifying event a KOL is credited for — and the same
   * request writes the set-once `arena_ticket_link` People property. A rejected
   * claim never fires it, which is what keeps a link from being credited for a
   * payout it did not make.
   */
  CEDE_ARENA_TICKET_LINK_CLAIMED: "Cede Arena - Ticket Link Claimed",
  /**
   * Fired once when the persona questionnaire opens. The denominator for both
   * submission rate and the fail-open drop (the gate resolves to "not owed" on a
   * failed fetch, so a missing SHOWN against new sign-ins is how that shows up).
   * Properties: variant.
   */
  CEDE_ARENA_QUALIFICATION_SHOWN: "Cede Arena - Qualification Shown",
  /** Persona questionnaire submitted. Properties: variant, persona, question_count. */
  CEDE_ARENA_QUALIFICATION_SUBMITTED: "Cede Arena - Qualification Submitted",
  /** Persona questionnaire skipped or dismissed. Properties: variant, at_stage. */
  CEDE_ARENA_QUALIFICATION_SKIPPED: "Cede Arena - Qualification Skipped",
  /** Fired once when the post-competition survey opens — the denominator for submission rate. */
  CEDE_ARENA_SURVEY_SHOWN: "Cede Arena - Survey Shown",
  /** Fired when the post-competition survey is submitted. Properties: per-question scores + has_comment. */
  CEDE_ARENA_SURVEY_SUBMITTED: "Cede Arena - Survey Submitted",
  /** Fired when the user skips/dismisses the post-competition survey. */
  CEDE_ARENA_SURVEY_SKIPPED: "Cede Arena - Survey Skipped",

  // Cede Arena Onboarding Quest Events (DEV-685)
  /** Fired once per session the first time the quest becomes visible in the header (standalone chip, or merged into the queued chip's pips). */
  CEDE_ARENA_QUEST_SHOWN: "Cede Arena - Quest Shown",
  /** Fired when a quest CTA is clicked. `step` is "deposit" or "duel". */
  CEDE_ARENA_QUEST_CTA_CLICKED: "Cede Arena - Quest CTA Clicked",
  /** Fired the first time a poll observes the quest's deposit step flip to done. */
  CEDE_ARENA_QUEST_DEPOSIT_COMPLETED: "Cede Arena - Quest Deposit Completed",
  /** Fired when a fresh entry is stamped `questEligible` at creation and durably funded (PAID). */
  CEDE_ARENA_QUEST_DUEL_QUEUED: "Cede Arena - Quest Duel Queued",
  /** Fired when a `questEligible` entry's match proposal is created. Carries `playedUsdt` — below the required buy-in is the residual downgrade-disqualification signal. */
  CEDE_ARENA_QUEST_DUEL_MATCHED: "Cede Arena - Quest Duel Matched",
  /** Emitted by the API when the grant command mints the reward pack — the authoritative completion, independent of whether the client ever shows the reveal. */
  CEDE_ARENA_QUEST_COMPLETED: "Cede Arena - Quest Completed",
  /** Unwired: quest status is derived at read time with no stored expiry transition to observe. */
  CEDE_ARENA_QUEST_EXPIRED: "Cede Arena - Quest Expired",
  /**
   * The quest panel was opened from the header chip — the middle of the funnel
   * between `QUEST_SHOWN` (the chip appeared) and `QUEST_CTA_CLICKED` (a step
   * was acted on). Without it an ignored chip and an opened-then-abandoned
   * panel are indistinguishable.
   *
   * Both header quest controls fire it, split by `panel`: `starter` (the onboarding
   * chip) and `weekly` (the CMS-authored quests control, which also reports `via`,
   * since hover-opening it is a far weaker signal than a click).
   */
  CEDE_ARENA_QUEST_PANEL_OPENED: "Cede Arena - Quest Panel Opened",
  /**
   * The grant REVEAL rendered. Distinct from `QUEST_COMPLETED`, which the API
   * fires when the pack mints: the mint is authoritative, this is whether the
   * user was ever actually shown their reward.
   */
  CEDE_ARENA_QUEST_REWARD_SHOWN: "Cede Arena - Quest Reward Shown",
  /** How the reveal ended: `enter` or `dismissed` — the grant → next-duel conversion. */
  CEDE_ARENA_QUEST_REWARD_CLOSED: "Cede Arena - Quest Reward Closed",

  // Post-battle recap (DEV-685 follow-up)
  /** Fired once per campaign when the settled post-battle recap renders results — the denominator for the events below. */
  CEDE_ARENA_POST_BATTLE_VIEWED: "Cede Arena - Post Battle Viewed",
  /** The post-practice pitch's action, `source: "post_practice"`. `action` is "spin_and_go" (with `buyInUsdt`) or "battle_royale" (with `campaignName`). */
  CEDE_ARENA_POST_PRACTICE_PITCH_CLICKED: "Cede Arena - Post Practice Pitch Clicked",
  /** Fired when the settled recap's "Find more battles" CTA is clicked. */
  CEDE_ARENA_FIND_MORE_BATTLES_CLICKED: "Cede Arena - Find More Battles Clicked",
  /** Fired when a settled duel's loser proposes an inline rematch. */
  CEDE_ARENA_REMATCH_PROPOSED: "Cede Arena - Rematch Proposed",
  /** Fired on the invitee's response to an incoming rematch challenge. `action` includes "deposit_required" for the short-balance branch. */
  CEDE_ARENA_REMATCH_RESPONDED: "Cede Arena - Rematch Responded",
  /** Give up on a live duel. `action`: "opened", "cancelled", "counting" (confirmed, grace running), "undone" (cancelled in the grace), "confirmed" (sent). */
  CEDE_ARENA_DUEL_FORFEITED: "Cede Arena - Duel Forfeited",

  // Pre-start desk + armed opening move. Common properties: campaignName, format, tradeMode, pairSymbol, side.
  /** The prep desk opened from the pre-start stage's Prepare action. Properties: campaignName, format, tradeMode. */
  CEDE_ARENA_PREPARE_OPENED: "Cede Arena - Prepare Opened",
  /** The waiting room's stage first showed on a competition page, once per visit. Properties: campaignName, format, secondsToStart, seat, isPractice. */
  CEDE_ARENA_WAITING_ROOM_VIEWED: "Cede Arena - Waiting Room Viewed",
  /** An opening move was armed or changed. Adds: isChange, marginUsd, leverage. */
  CEDE_ARENA_OPENING_MOVE_ARMED: "Cede Arena - Opening Move Armed",
  /** The player cancelled their armed opening move. */
  CEDE_ARENA_OPENING_MOVE_CANCELLED: "Cede Arena - Opening Move Cancelled",
  /** The armed move was placed at the bell. Tracked by the API where it is saved; no tradeMode. */
  CEDE_ARENA_OPENING_MOVE_FILLED: "Cede Arena - Opening Move Filled",
  /** The armed move could not be placed at the bell. Tracked by the API; adds reason, the cause code (e.g. TOO_LATE). */
  CEDE_ARENA_OPENING_MOVE_FAILED: "Cede Arena - Opening Move Failed",

  // Arena support chat (DEV-679)
  /** Fired when the support widget panel opens. Properties: route, hasOpenConversation, via (`launcher` or the contextual anchor/nudge trigger), surface (`panel` desktop | `sheet` mobile — proxied events carry no device properties). */
  ARENA_SUPPORT_CHAT_OPENED: "Arena - Support Chat Opened",
  /** Fired when a topic button is chosen. Properties: topic, route. */
  ARENA_SUPPORT_TOPIC_SELECTED: "Arena - Support Topic Selected",
  /** Properties: topic, route, isFirstMessage. `route` ties the feedback to where in the flow the wall was hit — the ticket's stated success criterion. */
  ARENA_SUPPORT_MESSAGE_SENT: "Arena - Support Message Sent",
  /** Properties: topic, resolvedBy. */
  ARENA_SUPPORT_CONVERSATION_RESOLVED: "Arena - Support Conversation Resolved",
  /** Properties: topic, rating. */
  ARENA_SUPPORT_CONVERSATION_RATED: "Arena - Support Conversation Rated",

  // Proactive support nudges. `accepted ÷ shown` per trigger is what decides whether
  // a trigger keeps earning its place; without all three the ladder is untunable.
  /** Properties: trigger, variant (`teaser` | `anchor`), route. */
  ARENA_SUPPORT_NUDGE_SHOWN: "Arena - Support Nudge Shown",
  /** Properties: trigger, variant, route. */
  ARENA_SUPPORT_NUDGE_DISMISSED: "Arena - Support Nudge Dismissed",
  /** Properties: trigger, variant, route. */
  ARENA_SUPPORT_NUDGE_ACCEPTED: "Arena - Support Nudge Accepted",

  /** Property: filter (`paid` | `all`). */
  ARENA_WATCH_SETTLED_FILTER_CHANGED: "Arena - Watch Settled Filter Changed",

  // Arena lobby Feed and chat
  /** Once per full Feed mount, after consent: surface `aside` (the lobby's xl aside or its below-xl section) or `tab` (/feed); the peek fires none. */
  ARENA_FEED_VIEWED: "Arena - Feed Viewed",
  /** Properties: filter (`all` | `chat` | `duels` | `payouts`), surface (`aside` | `tab` | `peek`). */
  ARENA_FEED_FILTER_CHANGED: "Arena - Feed Filter Changed",
  /** Properties: type (feed item type), cta (`watch` | `result` | `match` | `tx`), surface (`aside` | `tab` | `peek`). */
  ARENA_FEED_ITEM_CLICKED: "Arena - Feed Item Clicked",
  /** Fired on an accepted send in any room. Properties: room (`global` | `duel`), isSpectator, kind. */
  ARENA_CHAT_MESSAGE_SENT: "Arena - Chat Message Sent",
} as const;

export type MetricsEvents = (typeof METRICS_EVENTS)[keyof typeof METRICS_EVENTS];

/**
 * Allowlist of route-level page-view events that should have `previousPage` auto-injected.
 * Maintained here alongside METRICS_EVENTS so new page-view events can be added to the set.
 *
 * Screen/modal views (TMA_UNAVAILABLE_VIEWED, AIRDROP_FARMING_INTRO_VIEWED) are intentionally excluded —
 * they are not route-level page views.
 */
export const PAGE_VIEW_EVENTS = new Set<MetricsEvents>([
  METRICS_EVENTS.VIEW_MAIN_PAGE,
  METRICS_EVENTS.VIEW_CAMPAIGNS_PAGE,
  METRICS_EVENTS.VIEW_TRADE_PAGE,
  METRICS_EVENTS.VIEW_PROFILE_PAGE,
  METRICS_EVENTS.VIEW_REFERRALS_PAGE,
  METRICS_EVENTS.VIEW_QUESTS_PAGE,
  METRICS_EVENTS.VIEW_PORTFOLIO_PAGE,
  METRICS_EVENTS.VIEW_HALL_OF_FAME_PAGE,
  METRICS_EVENTS.VIEW_LOTTERY_PAGE,
  METRICS_EVENTS.VIEW_HOLD_CAMPAIGNS_PAGE,
  METRICS_EVENTS.VIEW_PREDICTIONS_PAGE,
  METRICS_EVENTS.VIEW_SELF_HOST_CAMPAIGN_PAGE,
  METRICS_EVENTS.VIEW_CLICK_AND_EARN_PAGE,
  METRICS_EVENTS.VIEW_PRIVACY_POLICY_PAGE,
  METRICS_EVENTS.VIEW_TERMS_AND_CONDITIONS_PAGE,
  METRICS_EVENTS.CRYPTO_COM_REFEREE_PAGE_VIEWED,
  METRICS_EVENTS.VIEW_CAMPAIGN,
  METRICS_EVENTS.VIEW_HOLD_CAMPAIGN,
  METRICS_EVENTS.AIRDROP_FARMING_PAGE_VIEWED,
  METRICS_EVENTS.VIEW_HYPERLIQUID_PAGE,
  METRICS_EVENTS.VIEW_ONCHAIN_SWAP_PAGE,
  METRICS_EVENTS.VIEW_KRAKEN_PAGE,
  METRICS_EVENTS.VIEW_COINBASE_PAGE,
  METRICS_EVENTS.VIEW_LBANK_PAGE,
  METRICS_EVENTS.FLOW_LANDING_PAGE_VIEWED,
  METRICS_EVENTS.FLOW_LEADERBOARD_PAGE_VIEWED,
  METRICS_EVENTS.CEDE_FLOW_TRADE_AND_EARN_PAGE_VIEWED,
  METRICS_EVENTS.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_VIEWED,
  METRICS_EVENTS.CEDE_ARENA_PAGE_VIEWED,
  METRICS_EVENTS.ARENA_PORTFOLIO_PAGE_VIEWED,
  METRICS_EVENTS.ARENA_PROFILE_PAGE_VIEWED,
  METRICS_EVENTS.ARENA_REFERRALS_PAGE_VIEWED,
  METRICS_EVENTS.ARENA_CLANS_PAGE_VIEWED,
  METRICS_EVENTS.ARENA_LEADERBOARD_VIEWED,
  METRICS_EVENTS.ARENA_WEEKLY_LEADERBOARD_VIEWED,
  METRICS_EVENTS.BIG_ARENA_COMPETITION_PAGE_VIEWED,
  METRICS_EVENTS.TOURNAMENT_LANDING_PAGE_VIEWED,
]);
